import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Upload, X, Image as ImageIcon, Loader2, Link as LinkIcon } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CHUNK_SIZE = 64 * 1024; // 64KB chunks to stay under proxy limits

export const ImageUpload = ({ 
  value, 
  onChange, 
  label = "Image",
  placeholder = "Upload an image or paste URL",
  className = ""
}) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [mode, setMode] = useState('upload'); // 'upload' or 'url'
  const fileInputRef = useRef(null);

  const getAuthToken = () => {
    const authData = localStorage.getItem('auth-storage');
    if (authData) {
      const parsed = JSON.parse(authData);
      return parsed?.state?.token;
    }
    return null;
  };

  const uploadChunks = async (file) => {
    const token = getAuthToken();
    if (!token) {
      toast.error('Please log in to upload images');
      return null;
    }

    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    let fileId = null;

    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);
      
      // Convert chunk to base64
      const base64Chunk = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64 = reader.result.split(',')[1];
          resolve(base64);
        };
        reader.readAsDataURL(chunk);
      });

      const response = await fetch(`${API_URL}/api/upload/chunk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          filename: file.name,
          chunk: base64Chunk,
          chunk_index: i,
          total_chunks: totalChunks,
          file_id: fileId
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Upload failed');
      }

      const result = await response.json();
      fileId = result.file_id;
      setProgress(Math.round(((i + 1) / totalChunks) * 100));

      if (result.status === 'complete') {
        return `${API_URL}${result.url}`;
      }
    }

    return null;
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Get file extension
    const fileExt = file.name.split('.').pop()?.toLowerCase();
    
    // Validate file type - also allow HEIC/HEIF from iPhones (will be converted server-side)
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif'];
    const allowedExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif'];
    
    // Some browsers/devices report HEIC as empty string, application/octet-stream, or wrong type
    const isValidType = allowedTypes.includes(file.type) || allowedExtensions.includes(fileExt);
    
    if (!isValidType) {
      toast.error(`Please select a valid image file (JPG, PNG, GIF, WebP, HEIC). Got: ${file.type || 'unknown'}`);
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }

    setUploading(true);
    setProgress(0);

    try {
      console.log(`Uploading file: ${file.name}, type: ${file.type}, size: ${file.size}`);
      const url = await uploadChunks(file);
      if (url) {
        console.log(`Upload complete, URL: ${url}`);
        // Small delay to ensure cloud storage has propagated
        await new Promise(resolve => setTimeout(resolve, 500));
        onChange(url);
        toast.success('Image uploaded successfully');
      } else {
        toast.error('Upload failed - no URL returned');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.message || 'Failed to upload image');
    } finally {
      setUploading(false);
      setProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleUrlChange = (e) => {
    onChange(e.target.value);
  };

  const handleClear = () => {
    onChange('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className={`space-y-3 ${className}`}>
      <div className="flex items-center justify-between">
        <Label>{label}</Label>
        <div className="flex gap-1">
          <Button
            type="button"
            variant={mode === 'upload' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setMode('upload')}
            className="h-7 px-2 text-xs"
          >
            <Upload className="h-3 w-3 mr-1" />
            Upload
          </Button>
          <Button
            type="button"
            variant={mode === 'url' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setMode('url')}
            className="h-7 px-2 text-xs"
          >
            <LinkIcon className="h-3 w-3 mr-1" />
            URL
          </Button>
        </div>
      </div>

      {mode === 'upload' ? (
        <div className="space-y-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif,.heic,.heif"
            onChange={handleFileSelect}
            className="hidden"
            disabled={uploading}
          />
          
          <div 
            onClick={() => !uploading && fileInputRef.current?.click()}
            className={`
              border-2 border-dashed rounded-lg p-4 text-center cursor-pointer
              transition-colors duration-200
              ${uploading ? 'bg-muted cursor-not-allowed' : 'hover:border-primary hover:bg-muted/50'}
              ${value ? 'border-green-500' : 'border-muted-foreground/25'}
            `}
          >
            {uploading ? (
              <div className="space-y-2">
                <Loader2 className="h-8 w-8 mx-auto animate-spin text-primary" />
                <p className="text-sm text-muted-foreground">Uploading... {progress}%</p>
                <div className="w-full bg-muted rounded-full h-2">
                  <div 
                    className="bg-primary h-2 rounded-full transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Upload className="h-8 w-8 mx-auto text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  Click to upload or drag and drop
                </p>
                <p className="text-xs text-muted-foreground/70">
                  JPG, PNG, GIF, WebP, HEIC (max 5MB)
                </p>
              </div>
            )}
          </div>
        </div>
      ) : (
        <Input
          type="url"
          value={value || ''}
          onChange={handleUrlChange}
          placeholder="https://example.com/image.jpg"
        />
      )}

      {/* Preview */}
      {value && (
        <div className="relative inline-block">
          <div className="relative w-32 h-32 rounded-lg overflow-hidden border bg-muted">
            <img
              src={`${value}${value.includes('?') ? '&' : '?'}t=${Date.now()}`}
              alt="Preview"
              className="w-full h-full object-cover"
              onError={(e) => {
                // Retry once after a short delay (cloud storage might need time)
                const retryCount = parseInt(e.target.dataset.retry || '0');
                if (retryCount < 2) {
                  e.target.dataset.retry = String(retryCount + 1);
                  setTimeout(() => {
                    e.target.src = `${value}${value.includes('?') ? '&' : '?'}t=${Date.now()}`;
                  }, 1000);
                } else {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }
              }}
              onLoad={(e) => {
                e.target.style.display = 'block';
                if (e.target.nextSibling) {
                  e.target.nextSibling.style.display = 'none';
                }
              }}
            />
            <div className="absolute inset-0 hidden items-center justify-center bg-muted">
              <ImageIcon className="h-8 w-8 text-muted-foreground" />
            </div>
          </div>
          <Button
            type="button"
            variant="destructive"
            size="icon"
            className="absolute -top-2 -right-2 h-6 w-6 rounded-full"
            onClick={handleClear}
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
